/**
 * The range calendar: quick picks plus two months, the second of which the
 * stylesheet hides when there is no room. Both ends are picked
 * on the same calendar so the run shows as one band, which follows the pointer
 * while a day is being chosen.
 */
import { useState } from 'react';

import { CalendarMonth } from './CalendarMonth';
import { dateKeyParts, type DateKey } from './date-keys';
import {
  quickPicks,
  rangeFromQuickPick,
  type DateTimeRange,
  type RangeEnd,
} from './date-time-range';
import { MonthNav } from './MonthNav';
import { QuickPicks } from './QuickPicks';

export interface RangeCalendarProps {
  range: DateTimeRange;
  editing: RangeEnd;
  today: DateKey;
  /** What the range is, for the "Pick the first day of …" hint. */
  noun: string;
  onPickDay: (day: DateKey) => void;
  onRange: (range: DateTimeRange) => void;
}

/** Both months are always drawn; the second is hidden when the picker is narrow. */
const MONTHS_RENDERED = 2;

/** Months counted from year 0, so stepping across a year is plain arithmetic. */
const monthIndex = (key: DateKey) => {
  const { year, month } = dateKeyParts(key);
  return year * 12 + month;
};

/** The band as drawn: the saved range, stretched toward the hovered day. */
function band(range: DateTimeRange, editing: RangeEnd, hover: DateKey | null) {
  const a = range.start.date;
  const b = range.end.date;
  if (!hover) return { a, b };
  if (editing === 'end') return { a, b: hover >= a ? hover : b };
  return { a: hover, b: hover > b ? hover : b };
}

/**
 * Which month to show first. Stays put while the edited end is on screen, and
 * jumps to it when a quick pick or a typed date moves it out of view.
 */
function useFirstShownMonth(range: DateTimeRange, editing: RangeEnd) {
  const edited = editing === 'end' ? range.end.date : range.start.date;
  const [first, setFirst] = useState(() => monthIndex(range.start.date));
  const [aimedAt, setAimedAt] = useState(edited);
  if (aimedAt !== edited) {
    setAimedAt(edited);
    const target = monthIndex(edited);
    if (target < first || target > first + MONTHS_RENDERED - 1) setFirst(target);
  }
  return [first, setFirst] as const;
}

export function RangeCalendar(p: RangeCalendarProps) {
  const [first, setFirst] = useFirstShownMonth(p.range, p.editing);
  const [hover, setHover] = useState<DateKey | null>(null);
  const { a, b } = band(p.range, p.editing, hover);
  const shown = Array.from({ length: MONTHS_RENDERED }, (_, i) => first + i);

  return (
    <div className="dtr-cal">
      <QuickPicks
        picks={quickPicks(p.today)}
        range={p.range}
        onPick={(pick) => p.onRange(rangeFromQuickPick(pick))}
      />
      <div className="dtr-cal-body">
        <MonthNav shown={shown} onStep={(by) => setFirst(first + by)} />
        <div className="dtr-months">
          {shown.map((m) => (
            <CalendarMonth
              key={m}
              year={Math.floor(m / 12)}
              month={m % 12}
              bandStart={a}
              bandEnd={b}
              selected={[p.range.start.date, p.range.end.date]}
              today={p.today}
              onPick={p.onPickDay}
              onHover={setHover}
            />
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 11.5,
            color: 'var(--nb-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            aria-hidden="true"
            style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--nb-primary)' }}
          />
          Pick the {p.editing === 'start' ? 'first' : 'last'} day of the {p.noun}
        </div>
      </div>
    </div>
  );
}
